import {
  DEFAULT_RESOURCE_ACCESS,
  ResourceAccess,
  ResourceVisibility,
} from '../interfaces/ResourceAccess';
import User from '../interfaces/User';
import { TranslationKey } from '../types/i18n';
import { canPerformAction, Permission, PERMISSIONS } from './permissions';

export function normalizeResourceAccess(
  access?: Partial<ResourceAccess> | null
): ResourceAccess {
  if (!access) {
    return { ...DEFAULT_RESOURCE_ACCESS };
  }
  return {
    visibility: access.visibility ?? DEFAULT_RESOURCE_ACCESS.visibility,
    shared_with_user_ids: access.shared_with_user_ids ?? [],
  };
}

export function toAccessPayload(access: ResourceAccess): ResourceAccess {
  return {
    visibility: access.visibility,
    shared_with_user_ids:
      access.visibility === 'shared' ? access.shared_with_user_ids : [],
  };
}

export function canManageResourceAccess(
  user: User | null | undefined,
  resource: { user_id?: string } | null | undefined,
  adminPermission: Permission
): boolean {
  if (!user) {
    return false;
  }
  if (user.permissions?.super_admin) {
    return true;
  }
  if (canPerformAction(user, adminPermission)) {
    return true;
  }
  if (resource?.user_id && user.id === resource.user_id) {
    return true;
  }
  return !resource;
}

export function canManageAgentAccess(
  user: User | null | undefined,
  resource: { user_id?: string } | null | undefined
): boolean {
  return canManageResourceAccess(user, resource, PERMISSIONS.MANAGE_AGENTS);
}

export function canManageKnowledgeAccess(
  user: User | null | undefined,
  resource: { user_id?: string } | null | undefined
): boolean {
  return canManageResourceAccess(user, resource, PERMISSIONS.MANAGE_KNOWLEDGE);
}

export function canEditAgentResource(
  user: User | null | undefined,
  agent:
    | { user_id?: string; access?: Partial<ResourceAccess> | null }
    | null
    | undefined
): boolean {
  if (!user || !agent) {
    return false;
  }
  if (user.permissions?.super_admin) {
    return true;
  }
  if (canPerformAction(user, PERMISSIONS.MANAGE_AGENTS)) {
    return true;
  }
  if (agent.user_id && agent.user_id === user.id) {
    return true;
  }
  const access = normalizeResourceAccess(agent.access);
  return (
    access.visibility === 'shared' &&
    access.shared_with_user_ids.includes(user.id)
  );
}

export function canEditKnowledgeResource(
  user: User | null | undefined,
  knowledge:
    | { user_id?: string; access?: Partial<ResourceAccess> | null }
    | null
    | undefined
): boolean {
  if (!user || !knowledge) {
    return false;
  }
  if (user.permissions?.super_admin) {
    return true;
  }
  if (canPerformAction(user, PERMISSIONS.MANAGE_KNOWLEDGE)) {
    return true;
  }
  if (knowledge.user_id && knowledge.user_id === user.id) {
    return true;
  }
  const access = normalizeResourceAccess(knowledge.access);
  return (
    access.visibility === 'shared' &&
    access.shared_with_user_ids.includes(user.id)
  );
}

export function getResourceVisibility(
  resource: { access?: Partial<ResourceAccess> | null } | null | undefined
): ResourceVisibility {
  return normalizeResourceAccess(resource?.access).visibility;
}

export function getResourceVisibilityTranslationKey(
  visibility: ResourceVisibility
): TranslationKey {
  const labels: Record<ResourceVisibility, TranslationKey> = {
    private: 'Private',
    company: 'Company',
    shared: 'Shared',
  };
  return labels[visibility];
}
